import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Container, Box, Typography } from '@mui/material';
import CreditCardComparisonTool from '../components/CreditCardComparison/CreditCardComparisonTool';
import Shortlist from '../components/Comparator/Shortlist';
import CrossLinks from '../components/Layout/CrossLinks';
import NewsletterSection from '../components/Layout/NewsletterSection';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import AtomicAnswer from '../components/SEO/AtomicAnswer';
import FAQSection from '../components/SEO/FAQSection';
import MethodologySection from '../components/CreditCardComparison/MethodologySection';
import TrustStrip from '../components/Layout/TrustStrip';
import SectionDivider from '../components/Home/SectionDivider';
import EditorialReview from '../components/Layout/EditorialReview';
import { colors } from '../theme/colorTokens';

/* ── i18n content ───────────────────────────────────────────────────── */

const content = {
  es: {
    seoTitle: 'Comparador de Tarjetas de Crédito en Canadá',
    seoDesc: 'Compara más de 120 tarjetas de crédito canadienses. Encuentra la mejor tarjeta para recién llegados, cash back, viajes y sin cuota anual.',
    schemaName: 'Comparador de Tarjetas de Crédito - FinMaple',
    schemaDesc: 'Compara más de 120 tarjetas de crédito canadienses. Filtra por cuota anual, recompensas y accesibilidad para recién llegados.',
    headerTitle: 'Las mejores tarjetas de crédito en Canadá - comparadas y verificadas para 2026',
    headerSubtitle: 'Analizamos tasas, costos reales y elegibilidad para recién llegados.',
    breadcrumb: 'Tarjetas de Crédito',
    atomic: 'En Canadá, la mejor forma de empezar a construir crédito como recién llegado es con una tarjeta de crédito asegurada (secured). Depositas un monto como garantía y el banco te da una tarjeta con ese límite. Paga el saldo completo cada mes y en 6-12 meses tendrás historial crediticio canadiense.',
    faqs: [
      {
        question: '¿Puedo obtener una tarjeta de crédito en Canadá sin historial crediticio?',
        answer: 'Sí. Varios bancos canadienses ofrecen tarjetas de crédito aseguradas (secured) diseñadas para personas sin historial crediticio. Solo necesitas depositar una cantidad como garantía (por ejemplo, $300-500 CAD) y el banco te da una tarjeta con ese límite. Bancos como Scotiabank, BMO y CIBC tienen programas para recién llegados.',
      },
      {
        question: '¿Cuál es la mejor tarjeta para recién llegados en Canadá?',
        answer: 'Depende de tus prioridades. Si buscas sin cuota anual y construir crédito, la Neo Secured Credit Card o la BMO CashBack Mastercard para newcomers son excelentes opciones. Si prefieres recompensas de viaje, la Scotiabank Gold American Express para newcomers ofrece buenos beneficios. Usa nuestro comparador para encontrar la ideal según tu perfil.',
      },
      {
        question: '¿Cómo funciona el credit score en Canadá?',
        answer: 'El credit score en Canadá va de 300 a 900 puntos. Se calcula considerando tu historial de pagos (35%), uso del crédito (30%), antigüedad de cuentas (15%), tipos de crédito (10%) y consultas recientes (10%). Un puntaje de 680+ se considera bueno. Puedes verificar tu score gratis con Borrowell o Credit Karma.',
      },
      {
        question: '¿Cuánto tiempo tarda en aprobarse una tarjeta de crédito?',
        answer: 'La aprobación típicamente toma entre 1 y 10 días hábiles. Las tarjetas aseguradas para newcomers suelen aprobarse más rápido (1-3 días) ya que el depósito reduce el riesgo del banco. Una vez aprobada, recibes la tarjeta física por correo en 7-14 días.',
      },
      {
        question: '¿Es mejor una tarjeta asegurada o prepagada para construir crédito?',
        answer: 'La tarjeta asegurada (secured) es mucho mejor para construir crédito. Las tarjetas prepagadas NO reportan a las agencias de crédito, por lo que no ayudan a construir historial. La tarjeta asegurada funciona como una tarjeta regular y reporta tus pagos a Equifax y TransUnion, ayudándote a subir tu puntaje.',
      },
    ],
  },
  en: {
    seoTitle: 'Credit Card Comparison Tool in Canada',
    seoDesc: 'Compare over 120 Canadian credit cards. Find the best card for newcomers, cash back, travel rewards and no annual fee.',
    schemaName: 'Credit Card Comparison Tool - FinMaple',
    schemaDesc: 'Compare over 120 Canadian credit cards. Filter by annual fee, rewards and newcomer accessibility.',
    headerTitle: 'The best credit cards in Canada — compared and verified for 2026',
    headerSubtitle: 'We analyze rates, real costs and eligibility for newcomers.',
    breadcrumb: 'Credit Cards',
    atomic: 'In Canada, the best way to start building credit as a newcomer is with a secured credit card. You deposit an amount as collateral and the bank gives you a card with that limit. Pay your full balance every month and in 6-12 months you\'ll have Canadian credit history.',
    faqs: [
      {
        question: 'Can I get a credit card in Canada without credit history?',
        answer: 'Yes. Several Canadian banks offer secured credit cards designed for people without credit history. You just need to deposit an amount as collateral (e.g., $300-500 CAD) and the bank gives you a card with that limit. Banks like Scotiabank, BMO and CIBC have newcomer programs.',
      },
      {
        question: 'What is the best credit card for newcomers in Canada?',
        answer: 'It depends on your priorities. If you want no annual fee and to build credit, the Neo Secured Credit Card or the BMO CashBack Mastercard for newcomers are great options. If you prefer travel rewards, the Scotiabank Gold American Express for newcomers offers good benefits. Use our comparison tool to find the best one for your profile.',
      },
      {
        question: 'How does the credit score work in Canada?',
        answer: 'The credit score in Canada ranges from 300 to 900 points. It\'s calculated based on your payment history (35%), credit utilization (30%), account age (15%), credit mix (10%) and recent inquiries (10%). A score of 680+ is considered good. You can check your score for free with Borrowell or Credit Karma.',
      },
      {
        question: 'How long does it take to get approved for a credit card?',
        answer: 'Approval typically takes 1 to 10 business days. Secured cards for newcomers are usually approved faster (1-3 days) since the deposit reduces the bank\'s risk. Once approved, you\'ll receive the physical card by mail in 7-14 days.',
      },
      {
        question: 'Is a secured or prepaid card better for building credit?',
        answer: 'A secured card is much better for building credit. Prepaid cards do NOT report to credit bureaus, so they don\'t help build credit history. A secured card works like a regular card and reports your payments to Equifax and TransUnion, helping you raise your score.',
      },
    ],
  },
  fr: {
    seoTitle: 'Comparateur de cartes de crédit au Canada',
    seoDesc: 'Comparez plus de 120 cartes de crédit canadiennes. Trouvez la meilleure carte pour les nouveaux arrivants, remises en argent, voyages et sans frais annuels.',
    schemaName: 'Comparateur de cartes de crédit - FinMaple',
    schemaDesc: 'Comparez plus de 120 cartes de crédit canadiennes. Filtrez par frais annuels, récompenses et accessibilité pour les nouveaux arrivants.',
    headerTitle: 'Les meilleures cartes de crédit au Canada — comparées et vérifiées pour 2026',
    headerSubtitle: 'Nous analysons les taux, les coûts réels et l\'admissibilité pour les nouveaux arrivants.',
    breadcrumb: 'Cartes de crédit',
    atomic: 'Au Canada, la meilleure façon de commencer à bâtir votre crédit en tant que nouvel arrivant est avec une carte de crédit garantie (secured). Vous déposez un montant en garantie et la banque vous donne une carte avec cette limite. Payez le solde complet chaque mois et en 6 à 12 mois, vous aurez un historique de crédit canadien.',
    faqs: [
      {
        question: 'Puis-je obtenir une carte de crédit au Canada sans historique de crédit?',
        answer: 'Oui. Plusieurs banques canadiennes offrent des cartes de crédit garanties (secured) conçues pour les personnes sans historique de crédit. Vous n\'avez qu\'à déposer un montant en garantie (par exemple, 300 à 500 $ CAD) et la banque vous donne une carte avec cette limite. Des banques comme Scotiabank, BMO et CIBC ont des programmes pour les nouveaux arrivants.',
      },
      {
        question: 'Quelle est la meilleure carte de crédit pour les nouveaux arrivants au Canada?',
        answer: 'Cela dépend de vos priorités. Si vous cherchez sans frais annuels et à bâtir votre crédit, la Neo Secured Credit Card ou la BMO CashBack Mastercard pour nouveaux arrivants sont d\'excellentes options. Si vous préférez les récompenses de voyage, la Scotiabank Gold American Express pour nouveaux arrivants offre de bons avantages. Utilisez notre comparateur pour trouver la carte idéale selon votre profil.',
      },
      {
        question: 'Comment fonctionne le pointage de crédit au Canada?',
        answer: 'Le pointage de crédit au Canada va de 300 à 900 points. Il est calculé en fonction de votre historique de paiements (35 %), de l\'utilisation du crédit (30 %), de l\'ancienneté des comptes (15 %), des types de crédit (10 %) et des demandes récentes (10 %). Un pointage de 680+ est considéré bon. Vous pouvez vérifier votre pointage gratuitement avec Borrowell ou Credit Karma.',
      },
      {
        question: 'Combien de temps faut-il pour obtenir l\'approbation d\'une carte de crédit?',
        answer: 'L\'approbation prend généralement de 1 à 10 jours ouvrables. Les cartes garanties pour nouveaux arrivants sont habituellement approuvées plus rapidement (1 à 3 jours) puisque le dépôt réduit le risque de la banque. Une fois approuvée, vous recevrez la carte physique par la poste en 7 à 14 jours.',
      },
      {
        question: 'Est-ce qu\'une carte garantie ou prépayée est mieux pour bâtir le crédit?',
        answer: 'La carte garantie (secured) est bien meilleure pour bâtir le crédit. Les cartes prépayées ne sont PAS signalées aux agences de crédit, elles n\'aident donc pas à bâtir un historique. La carte garantie fonctionne comme une carte régulière et signale vos paiements à Equifax et TransUnion, vous aidant à augmenter votre pointage.',
      },
    ],
  },
};

/* ── Page ────────────────────────────────────────────────────────────── */

const CreditCardsPage = () => {
  const { i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const c = content[lng] || content.es;
  const { hash } = useLocation();

  // Auto-scroll to wizard/comparador when landing without a hash
  useEffect(() => {
    if (!hash) {
      const timer = setTimeout(() => {
        const el = document.getElementById('comparador');
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: c.schemaName,
    description: c.schemaDesc,
    provider: {
      '@type': 'Organization',
      name: 'FinMaple',
      url: 'https://www.finmaple.com',
    },
  };

  return (
    <Box sx={{ bgcolor: '#FFFFFF' }}>
      <SEOHead
        seoKey="creditCards"
        title={c.seoTitle}
        description={c.seoDesc}
        canonical="/tarjetas-de-credito"
        routeKey="creditCards"
        schema={schema}
        noIndex={lng !== 'es'}
      />
      <PageHeader
        variant="functional"
        title={c.headerTitle}
        subtitle={c.headerSubtitle}
        breadcrumbs={[{ label: c.breadcrumb }]}
        maxWidth="lg"
      />
      <SectionDivider from="#F7F9FC" to="#FFFFFF" />
      <EditorialReview />
      <Container maxWidth="lg" sx={{ mb: 2 }}>
        <AtomicAnswer text={c.atomic} />
      </Container>
      <Container maxWidth="lg" sx={{ mb: { xs: 3, md: 4 } }}>
        <Shortlist
          title={lng === 'fr' ? 'Nos recommandations rapides' : lng === 'en' ? 'Our quick picks' : 'Nuestras recomendaciones rápidas'}
          items={[
            {
              badge: lng === 'fr' ? 'Meilleure pour bâtir le crédit' : lng === 'en' ? 'Best to build credit' : 'Mejor para empezar historial',
              name: 'Neo Secured Credit Card',
              detail: lng === 'fr' ? 'Frais annuels de 96 $, remise en argent, idéale pour les nouveaux arrivants.' : lng === 'en' ? '$96/year annual fee, cashback, ideal for newcomers.' : 'Cuota anual $96, cashback desde el día 1, ideal para newcomers sin historial.',
              cta: lng === 'fr' ? 'Voir la carte' : lng === 'en' ? 'See card' : 'Ver tarjeta',
              link: '#comparador',
            },
            {
              badge: lng === 'fr' ? 'Meilleure sans frais annuels' : lng === 'en' ? 'Best no annual fee' : 'Mejor sin cuota anual',
              name: 'BMO CashBack Mastercard',
              detail: lng === 'fr' ? '0 $ frais annuels, 0,5 % remise en argent, approuvée pour nouveaux arrivants.' : lng === 'en' ? '$0 annual fee, 0.5% cashback, approved for newcomers.' : '$0 cuota anual, 0.5% cashback, aprobada para newcomers con programa BMO.',
              cta: lng === 'fr' ? 'Voir la carte' : lng === 'en' ? 'See card' : 'Ver tarjeta',
              link: '#comparador',
            },
            {
              badge: lng === 'fr' ? 'Meilleure remise en argent' : lng === 'en' ? 'Best cashback' : 'Mejor cashback',
              name: 'Tangerine Money-Back Credit Card',
              detail: lng === 'fr' ? '2 % dans 2 catégories, aucuns frais annuels, pas besoin d\'être client.' : lng === 'en' ? '2% in 2 categories, no annual fee, no need to be a customer.' : '2% en 2 categorías, sin cuota anual, no necesitas ser cliente Tangerine.',
              cta: lng === 'fr' ? 'Voir la carte' : lng === 'en' ? 'See card' : 'Ver tarjeta',
              link: '#comparador',
            },
          ]}
        />
      </Container>
      <Box id="comparador">
        <CreditCardComparisonTool />
      </Box>
      <Container maxWidth="lg">
        <Box sx={{ bgcolor: colors.softBg, borderRadius: '12px', p: { xs: 2.5, md: 3 }, mt: 3, mb: 3 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '15px', color: colors.textPrimary, mb: 1 }}>
            {lng === 'fr' ? 'Avant de faire une demande' : lng === 'en' ? 'Before you apply' : 'Antes de aplicar'}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.7 }}>
            {lng === 'fr'
              ? 'La meilleure carte n\'est pas celle avec le plus de points. C\'est celle que vous pouvez bien utiliser, payer à temps et maintenir pendant que vous bâtissez votre historique.'
              : lng === 'en'
                ? 'The best card isn\'t the one with the most points. It\'s the one you can use well, pay on time and keep while you build your history.'
                : 'La mejor tarjeta no es la que promete más puntos. Es la que sí puedes usar bien, pagar a tiempo y mantener mientras construyes historial.'}
          </Typography>
        </Box>
        <CrossLinks
          title={lng === 'fr' ? 'Prochaine étape' : lng === 'en' ? 'Next step' : 'Siguiente paso'}
          links={[
            { label: lng === 'fr' ? 'Bâtir votre crédit' : lng === 'en' ? 'Build your credit' : 'Guía construir crédito', to: '/blog/construir-credito-canada-desde-cero' },
            { label: lng === 'fr' ? 'Pointage de crédit gratuit' : lng === 'en' ? 'Free credit score' : 'Credit score gratis (Borrowell)', to: 'https://borrowell.com/refer-a-friend/free-credit-score?utm_campaign=Refer5&utm_medium=web&utm_source=refer2022-3818681', external: true },
            { label: lng === 'fr' ? 'Comparer les comptes' : lng === 'en' ? 'Compare accounts' : 'Comparador de cuentas', to: '/comparar-cuentas' },
            { label: lng === 'fr' ? 'Commencer ici' : lng === 'en' ? 'Get started' : 'Empieza aquí', to: '/empieza-aqui' },
          ]}
        />
        <MethodologySection />
        <FAQSection faqs={c.faqs} />
      </Container>
      <TrustStrip />
      <NewsletterSection />
    </Box>
  );
};

export default CreditCardsPage;
